import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { BaseService } from './base.service';
import { SKIP_GLOBAL_ERROR_TOAST } from '@core/interceptors/error.interceptor';

export interface EncerrarOcorrenciaPayload {
  desfecho: number;
  acoesTomadas: string;
  dataResolucao: string;
  entidadesAccionadas?: string | null;
  valorDanosPerdas?: number | null;
  descricaoDanosPerdas?: string | null;
}
import {
  Efetivo,
  Posto,
  Escala,
  Ocorrencia,
  Equipamento,
  Cliente,
  AnexoCliente,
  ContratoCliente,
  ContratoEfetivo,
  Ronda,
  PontoCritico,
  AnexoEfetivo,
  AnexoEquipamento,
  EfetivoEquipamento,
  TipoEquipamento,
  CategoriaEquipamento,
  TipoPosto,
  FuncaoEfetivo,
  Categoria,
  Municipio,
  Provincia,
  PostoCobertura,
  TipoAnexoEfetivo,
  TipoAnexoEquipamento,
  DashboardStats,
  Turno,
  PropriedadeEquipamento,
  PropriedadeOpcao,
  PagedResponse,
  Utilizador,
} from '../models/models';

export type PapelEfetivoEscalaEnum = number;

export interface EscalaEfetivoBulkDto {
  efetivoId: number;
  papelEfetivoEscalaEnum: PapelEfetivoEscalaEnum;
}

export interface EscalaBulkItemDto {
  idPosto: number;
  turnoId: number;
  /** ISO local sem timezone: yyyy-MM-ddTHH:mm:ss */
  dataInicio: string;
  dataTermino: string;
  efetivos: EscalaEfetivoBulkDto[];
}

export interface EscalaEfetivoAddOrUpdateDto {
  efetivoId: number;
  horaInicioReal?: string | null;
  horaFimReal?: string | null;
  papelEfetivoEscalaEnum: PapelEfetivoEscalaEnum;
}

export interface EscalaUpdateDto {
  postoId: number;
  turnoId: number;
  /** ISO local sem timezone: yyyy-MM-ddTHH:mm:ss */
  dataInicio: string;
  dataTermino: string;
  estado: number;
  escalasEfetivosAddOrUpdate: EscalaEfetivoAddOrUpdateDto[];
}

export const SECTOR_TO_API: Record<string, string> = {
  banca: 'BANCA_E_FINANCAS',
  petroleo: 'PETROLEO_E_GAS',
  aviacao: 'AVIACAO_E_AEROPORTOS',
  telecomunicacoes: 'TELECOMUNICACOES',
  saude: 'SAUDE',
  comercio: 'COMERCIO_E_RETALHO',
  hotelaria: 'HOTELARIA_E_TURISMO',
  industria: 'INDUSTRIA_E_MANUFATURA',
  publico: 'SECTOR_PUBLICO',
  construcao: 'CONSTRUCAO',
  outro: 'OUTRO',
};

export const API_TO_SECTOR: Record<string | number, string> = {
  BANCA_E_FINANCAS: 'banca',
  PETROLEO_E_GAS: 'petroleo',
  AVIACAO_E_AEROPORTOS: 'aviacao',
  TELECOMUNICACOES: 'telecomunicacoes',
  SAUDE: 'saude',
  COMERCIO_E_RETALHO: 'comercio',
  HOTELARIA_E_TURISMO: 'hotelaria',
  INDUSTRIA_E_MANUFATURA: 'industria',
  SECTOR_PUBLICO: 'publico',
  CONSTRUCAO: 'construcao',
  OUTRO: 'outro',
  1: 'banca',
  2: 'petroleo',
  3: 'aviacao',
  4: 'telecomunicacoes',
  5: 'saude',
  6: 'comercio',
  7: 'hotelaria',
  8: 'industria',
  9: 'publico',
  10: 'construcao',
  11: 'outro',
  // O endpoint devolve "Outro" com o valor reservado 99.
  99: 'outro',
};

@Injectable({ providedIn: 'root' })
export class EfetivoService extends BaseService<any> {
  protected readonly endpoint = 'Efetivos';

  store(fd: FormData): Observable<any> {
    return this.http.post<any>(this.baseUrl, fd);
  }

  toggleStatus(id: number): Observable<any> {
    return this.http.patch<any>(
      `${environment.apiurl}/api/Efetivos/${id}/toggle-ativo`,
      {},
    );
  }

  reativar(id: number): Observable<any> {
    return this.http.patch<any>(
      `${environment.apiurl}/api/Efetivos/${id}/reativar`,
      {},
    );
  }

  afastar(id: number, motivo: string): Observable<any> {
    return this.http.patch<any>(
      `${environment.apiurl}/api/Efetivos/${id}/afastar`,
      { motivo },
    );
  }

  suspender(
    id: number,
    body: { motivoSuspensao: number; observacao: string },
  ): Observable<any> {
    return this.http.patch<any>(
      `${environment.apiurl}/api/Efetivos/${id}/suspender`,
      body,
    );
  }

  uploadFoto(
    id: number,
    file: File,
    idTipoAnexo: number,
    designacao: string,
  ): Observable<any> {
    const formData = new FormData();
    formData.append('IdTipoAnexo', String(idTipoAnexo));
    formData.append('Designacao', designacao);
    formData.append('Ficheiro', file, file.name);

    return this.http.post(`${this.baseUrl}/${id}/anexos`, formData);
  }

  getTipoAnexo(descricao: string): Observable<any> {
    return this.http.get<any>(
      `${environment.apiurl}/TipoAnexo/RetornarIdTipoAnexeoEfetivo/${descricao}`,
    );
  }

  obterEstatisticasEfetivo(
    efetivoId: number,
    escalaId: number,
  ): Observable<any> {
    return this.http.get<any>(
      `${environment.apiurl}/api/MobileEfetivo/Escala/${efetivoId}/ObterEstatisticasEfetivo/${escalaId}`,
    );
  }
}

@Injectable({ providedIn: 'root' })
export class PostoService extends BaseService<Posto> {
  protected readonly endpoint = 'Postos';

  getAtalhoPeriodoEnum(): Observable<any> {
    return this.http.get<any>(`${environment.apiurl}/api/Enum/AtalhoPeriodo`);
  }

  getTimeline(
    idPosto: number,
    filtro?: {
      dataInicio?: string;
      dataFim?: string;
      atalho?: number;
      tipoEvento?: number;
    },
  ): Observable<any> {
    let params = new HttpParams();
    if (filtro) {
      if (filtro.dataInicio)
        params = params.set('dataInicio', filtro.dataInicio);
      if (filtro.dataFim) params = params.set('dataFim', filtro.dataFim);
      if (filtro.atalho !== undefined && filtro.atalho !== null) {
        params = params.set('atalho', String(filtro.atalho));
      }
      if (filtro.tipoEvento !== undefined && filtro.tipoEvento !== null) {
        params = params.set('tipoEvento', String(filtro.tipoEvento));
      }
    }
    return this.http.get<any>(
      `${environment.apiurl}/api/Postos/${idPosto}/timeline`,
      { params },
    );
  }

  getEstatisticas(
    idPosto: number,
    filtro?: {
      dataInicio?: string;
      dataFim?: string;
      atalho?: number;
    },
  ): Observable<any> {
    let params = new HttpParams();
    if (filtro) {
      if (filtro.dataInicio)
        params = params.set('dataInicio', filtro.dataInicio);
      if (filtro.dataFim) params = params.set('dataFim', filtro.dataFim);
      if (filtro.atalho !== undefined && filtro.atalho !== null) {
        params = params.set('atalho', String(filtro.atalho));
      }
    }
    return this.http.get<any>(
      `${environment.apiurl}/api/Postos/${idPosto}/estatisticas`,
      { params },
    );
  }

  listarEquipamentos(idPosto: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.apiurl}/api/Postos/ListarEquipamentoPosto`,
      {
        params: { idPosto: String(idPosto) },
      },
    );
  }

  listarEquiNecessidadeByPosto(idPosto: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.apiurl}/api/PostosEquipamentosNecessidade`,
      {
        params: { idPosto: String(idPosto) },
      },
    );
  }

  adicionarNecessidade(
    postoId: number,
    itens: { equipamentoId: number; quantidade: number }[],
  ): Observable<any> {
    const payload = {
      idPosto: postoId,
      equipamentos: itens.map((item) => ({
        equipamentoId: item.equipamentoId,
        quantidade: item.quantidade,
      })),
    };
    return this.http.post(
      `${environment.apiurl}/api/PostosEquipamentosNecessidade`,
      payload,
    );
  }

  removerEquipamentoNecessidade(equipamentoId: number): Observable<any> {
    return this.http.delete(
      `${environment.apiurl}/api/PostosEquipamentosNecessidade/${equipamentoId}`,
    );
  }

  adicionarEquipamentos(
    postoId: number,
    itens: { idTipoEquipamento: number; quantidade: number }[],
  ): Observable<any> {
    return this.http.post(
      `${environment.apiurl}/api/Postos/AdicionarTiposEquipamentos/${postoId}`,
      itens,
    );
  }

  toggleStatusPosto(postoId: number): Observable<any> {
    return this.http.patch<any>(
      `${environment.apiurl}/api/Postos/AtivarOuDesativar/${postoId}`,
      {},
    );
  }

  removerEquipamento(idTipoEquipamento: number): Observable<any> {
    return this.http.delete<any>(
      `${environment.apiurl}/api/Postos/RemoverTiposEquipamentos/${idTipoEquipamento}`,
    );
  }
}

@Injectable({ providedIn: 'root' })
export class EscalaService extends BaseService<Escala> {
  protected readonly endpoint = 'Escalas';

  getEfetivosAlocadosPorPosto(
    postoId: number,
    params?: { pageNumber?: number; pageSize?: number; NomeEfetivo?: string }
  ): Observable<any> {
    let httpParams = new HttpParams();

    if (params?.pageNumber != null) {
      httpParams = httpParams.set('pageNumber', String(params.pageNumber));
    }
    if (params?.pageSize != null) {
      httpParams = httpParams.set('pageSize', String(params.pageSize));
    }
    if (params?.NomeEfetivo?.trim()) {
      httpParams = httpParams.set('NomeEfetivo', params.NomeEfetivo.trim());
    }

    return this.http.get<any>(
      `${environment.apiurl}/api/escalas/por-posto/${postoId}/efetivos-alocados`,
      { params: httpParams },
    );
  }

  getHistorico(escalaId: number): Observable<any> {
    return this.http.get<any>(
      `${environment.apiurl}/api/Escalas/${escalaId}/historico`,
    );
  }

  createBulk(items: EscalaBulkItemDto[]): Observable<any> {
    return this.http.post<any>(`${environment.apiurl}/api/Escalas/bulk`, items);
  }

  updateEscala(escalaId: number, payload: EscalaUpdateDto): Observable<any> {
    return this.http.put<any>(
      `${environment.apiurl}/api/Escalas/${escalaId}`,
      payload,
    );
  }

  removerEfetivosEscala(
    escalaId: number,
    removerEfetivosEscalaIds: number[],
  ): Observable<any> {
    return this.http.request<any>(
      'DELETE',
      `${environment.apiurl}/api/Escalas/${escalaId}/RemoverEfetivoEscala`,
      {
        body: { removerEfetivosEscalaIds },
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  adicionarEfetivo(escalaId: number, efetivoId: number): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/${escalaId}/${efetivoId}/AdicionarEfetivo`,
      {},
    );
  }

  removerEfetivo(escalaId: number, efetivoId: number): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/${escalaId}/${efetivoId}/RemoverEfetivo`,
    );
  }

  adicionarEquipamentoAoEfetivo(
    escalaEfetivoId: number,
    equipamentoId: number,
  ): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/${escalaEfetivoId}/${equipamentoId}/AdicionarEquipamentoAoEfetivo`,
      {},
    );
  }

  removerEquipamentoDoEfetivo(
    escalaEfetivoId: number,
    equipamentoId: number,
  ): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/${escalaEfetivoId}/${equipamentoId}/RemoverEquipamentoDoEfetivoAsync`,
    );
  }
}

@Injectable({ providedIn: 'root' })
export class OcorrenciaService extends BaseService<Ocorrencia> {
  protected readonly endpoint = 'ocorrencias';

  encerrar(id: number, payload: EncerrarOcorrenciaPayload): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/${id}/encerrar`, payload);
  }

  reabrir(id: number): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/${id}/reabrir`, {});
  }
}

@Injectable({ providedIn: 'root' })
export class OcorrenciaAnexoService extends BaseService<any> {
  protected readonly endpoint = 'ocorrencias-anexos';
}

@Injectable({ providedIn: 'root' })
export class EquipamentoService extends BaseService<Equipamento> {
  protected readonly endpoint = 'Equipamentos';

  createWithFormData(fd: FormData): Observable<Equipamento> {
    return this.http.post<Equipamento>(this.baseUrl, fd);
  }

  updateWithFormData(id: number, fd: FormData): Observable<Equipamento> {
    return this.http.put<Equipamento>(`${this.baseUrl}/${id}`, fd);
  }

  uploadFoto(id: number, file: File): Observable<any> {
    const fd = new FormData();
    fd.append('foto', file, file.name);
    return this.http.post<any>(`${this.baseUrl}/${id}/foto`, fd);
  }
}

@Injectable({ providedIn: 'root' })
export class ClienteService extends BaseService<Cliente> {
  protected readonly endpoint = 'Clientes';

  createWithFormData(fd: FormData): Observable<any> {
    return this.http.post<any>(this.baseUrl, fd);
  }

  updateWithFormData(id: number, fd: FormData): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/${id}`, fd);
  }

  uploadLogo(id: number, file: File): Observable<Cliente> {
    const fd = new FormData();
    fd.append('logo', file);
    return this.http.post<Cliente>(
      `${environment.apiurl}/api/Clientes/${id}/logo`,
      fd,
    );
  }

  toggleStatus(id: number): Observable<any> {
    return this.http.patch<any>(
      `${environment.apiurl}/api/Clientes/${id}/toggle-status`,
      {},
    );
  }
}

@Injectable({ providedIn: 'root' })
export class AnexoClienteService extends BaseService<AnexoCliente> {
  protected readonly endpoint = 'anexos-cliente';

  getByCliente(idCliente: number): Observable<AnexoCliente[]> {
    return this.http.get<AnexoCliente[]>(
      `${environment.apiurl}/api/anexos-cliente/por-cliente/${idCliente}`,
    );
  }

  upload(fd: FormData): Observable<AnexoCliente> {
    return this.http.post<AnexoCliente>(
      `${environment.apiurl}/api/anexos-cliente`,
      fd,
    );
  }
}

@Injectable({ providedIn: 'root' })
export class ContratoService extends BaseService<ContratoCliente> {
  protected readonly endpoint = 'contratos-cliente';

  getByCliente(idCliente: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/por-cliente/${idCliente}`);
  }

  createWithFile(payload: any, file?: File | null): Observable<any> {
    if (!file) return this.create(payload);
    const fd = new FormData();
    Object.entries(payload).forEach(
      ([k, v]) => v != null && fd.append(k, String(v)),
    );
    fd.append('ficheiro', file);
    return this.http.post<any>(this.baseUrl, fd);
  }

  updateWithFile(
    id: number,
    payload: any,
    file?: File | null,
  ): Observable<any> {
    if (!file) return this.update(id, payload);
    const fd = new FormData();
    Object.entries(payload).forEach(
      ([k, v]) => v != null && fd.append(k, String(v)),
    );
    fd.append('ficheiro', file);
    return this.http.put<any>(`${this.baseUrl}/${id}`, fd);
  }
}

@Injectable({ providedIn: 'root' })
export class ContratoEfetivoService extends BaseService<ContratoEfetivo> {
  protected readonly endpoint = 'alocacoes';

  override getAll(params?: Record<string, unknown>): Observable<any> {
    const mapped: Record<string, unknown> = { ...params };
    if (mapped['idPosto'] !== undefined) {
      mapped['PostoId'] = mapped['idPosto'];
      delete mapped['idPosto'];
    }
    return super.getAll(mapped);
  }

  getByEfetivo(idEfetivo: number): Observable<any> {
    return this.http.get<any>(
      `${environment.apiurl}/api/alocacoes/por-efetivo/${idEfetivo}`,
    );
  }

  createWithFormData(fd: FormData): Observable<any> {
    return this.http.post<any>(this.baseUrl, fd);
  }

  updateWithFormData(id: number, fd: FormData): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/${id}`, fd);
  }

  remover(payload: {
    idContratoEfetivo: number;
    motivoRemocao: string;
    dataRemocao: string;
    observacaoRemocao: string;
    isRemocao: boolean;
  }): Observable<any> {
    return this.http.request('DELETE', `${this.baseUrl}/RemoverAlocacao`, {
      body: payload,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  createRange(
    idPosto: number,
    requests: {
      idEfetivo: number;
      turno?: string | null;
      dataInicio: string;
      dataFim?: string | null;
    }[],
  ): Observable<any> {
    const fd = new FormData();
    requests.forEach((r, i) => {
      fd.append(`requests[${i}].idEfetivo`, String(r.idEfetivo));
      if (r.turno) fd.append(`requests[${i}].turno`, r.turno);
      fd.append(`requests[${i}].dataInicio`, r.dataInicio);
      if (r.dataFim) fd.append(`requests[${i}].dataFim`, r.dataFim);
    });
    return this.http.post<any>(
      `${environment.apiurl}/api/alocacoes/${idPosto}/Range`,
      fd,
    );
  }

  createRangeFormData(idPosto: number, formData: FormData): Observable<any> {
    return this.http.post<any>(
      `${environment.apiurl}/api/alocacoes/${idPosto}/Range`,
      formData,
    );
  }
}

@Injectable({ providedIn: 'root' })
export class RondaService extends BaseService<any> {
  protected readonly endpoint = 'Ronda';
}

@Injectable({ providedIn: 'root' })
export class PontoCriticoService extends BaseService<PontoCritico> {
  protected readonly endpoint = 'pontos-criticos';
}

@Injectable({ providedIn: 'root' })
export class AnexoEfetivoService extends BaseService<AnexoEfetivo> {
  protected readonly endpoint = 'anexos-efetivo';

  getByEfetivo(idEfetivo: number): Observable<any> {
    return this.http.get<any>(
      `${environment.apiurl}/api/anexos-efetivo/por-efetivo/${idEfetivo}`,
    );
  }

  upload(fd: FormData): Observable<any> {
    return this.http.post<any>(`${environment.apiurl}/api/anexos-efetivo`, fd);
  }
}

@Injectable({ providedIn: 'root' })
export class AnexoEquipamentoService extends BaseService<AnexoEquipamento> {
  protected readonly endpoint = 'anexos-equipamento';

  getByEquipamento(idEquipamento: number): Observable<AnexoEquipamento[]> {
    return this.http.get<AnexoEquipamento[]>(
      `${environment.apiurl}/api/anexos-equipamento/por-equipamento/${idEquipamento}`,
    );
  }
}

@Injectable({ providedIn: 'root' })
export class EfetivoEquipService extends BaseService<EfetivoEquipamento> {
  protected readonly endpoint = 'efetivos-equipamento';

  getTurnoChecklist(params: Record<string, any>): Observable<any> {
    let hp = new HttpParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== null && v !== undefined) hp = hp.set(k, String(v));
    }
    return this.http.get<any>(
      `${environment.apiurl}/api/TurnoCheckListEquipamento`,
      { params: hp },
    );
  }
}

@Injectable({ providedIn: 'root' })
export class TipoEquipService extends BaseService<TipoEquipamento> {
  protected readonly endpoint = 'tipos-equipamento';
}

@Injectable({ providedIn: 'root' })
export class PropriedadeEquipService {
  private readonly http = inject(HttpClient);

  getByTipoEquipamento(
    tipoEquipamentoId: number,
  ): Observable<PagedResponse<PropriedadeEquipamento>> {
    const params = new HttpParams()
      .set('pageNumber', '1')
      .set('pageSize', '100')
      .set('tipoEquipamentoId', String(tipoEquipamentoId));
    return this.http.get<PagedResponse<PropriedadeEquipamento>>(
      `${environment.apiurl}/Propriedade`,
      { params },
    );
  }

  create(
    dto: Partial<PropriedadeEquipamento>,
  ): Observable<PropriedadeEquipamento> {
    return this.http.post<PropriedadeEquipamento>(
      `${environment.apiurl}/Propriedade`,
      dto,
    );
  }

  update(
    id: number,
    dto: Partial<PropriedadeEquipamento>,
  ): Observable<PropriedadeEquipamento> {
    return this.http.put<PropriedadeEquipamento>(
      `${environment.apiurl}/Propriedade/${id}`,
      dto,
    );
  }
}

@Injectable({ providedIn: 'root' })
export class PropriedadeOpcaoService extends BaseService<PropriedadeOpcao> {
  protected readonly endpoint = 'PropriedadeOpcoes';

  getByPropriedade(
    propriedadeId: number,
  ): Observable<PagedResponse<PropriedadeOpcao>> {
    return this.getAll({
      pageNumber: 1,
      pageSize: 100,
      propriedadeId,
    });
  }
}

@Injectable({ providedIn: 'root' })
export class CategoriaEquipService extends BaseService<CategoriaEquipamento> {
  protected readonly endpoint = 'categorias-equipamento';
}

@Injectable({ providedIn: 'root' })
export class TipoPostoService extends BaseService<TipoPosto> {
  protected readonly endpoint = 'TiposPosto';
}

@Injectable({ providedIn: 'root' })
export class FuncaoService extends BaseService<FuncaoEfetivo> {
  protected readonly endpoint = 'funcoes-efetivo';
}

@Injectable({ providedIn: 'root' })
export class CategoriaService extends BaseService<Categoria> {
  protected readonly endpoint = 'Categorias';
}

@Injectable({ providedIn: 'root' })
export class MunicipioService extends BaseService<Municipio> {
  protected readonly endpoint = 'Municipios';

  getByProvincia(idProvincia: number): Observable<Municipio[]> {
    return this.http.get<Municipio[]>(
      `${environment.apiurl}/api/Municipios/por-provincia/${idProvincia}`,
    );
  }
}

@Injectable({ providedIn: 'root' })
export class ProvinciaService extends BaseService<Provincia> {
  protected readonly endpoint = 'Provincias';
}

@Injectable({ providedIn: 'root' })
export class PostoCoberturasService extends BaseService<PostoCobertura> {
  protected readonly endpoint = 'postos-coberturas';
}

@Injectable({ providedIn: 'root' })
export class TipoAnexoEfetivoService extends BaseService<TipoAnexoEfetivo> {
  protected readonly endpoint = 'tipos-anexo-efetivo';
}

@Injectable({ providedIn: 'root' })
export class TipoAnexoEquipService extends BaseService<TipoAnexoEquipamento> {
  protected readonly endpoint = 'TiposAnexoEquipamento';
}

@Injectable({ providedIn: 'root' })
export class TurnoService extends BaseService<Turno> {
  protected readonly endpoint = 'Turnos';
}

@Injectable({ providedIn: 'root' })
export class DashboardService {
  protected readonly endpoint = 'Dashboard/stats';

  getStats(): Observable<DashboardStats> {
    return new Observable((obs) => {
      obs.next({
        efetivosAtivos: 0,
        postosAtivos: 0,
        ocorrenciasUrgentes: 0,
        contratosAVencer: 0,
        escalasHoje: 0,
      });
      obs.complete();
    });
  }
}

@Injectable({ providedIn: 'root' })
export class UtilizadorService extends BaseService<Utilizador> {
  protected override readonly endpoint = 'Utilizador';

  override getAll(params?: Record<string, unknown>): Observable<any> {
    const mapped: Record<string, unknown> = { ...params };
    if (mapped['TipoPerfilEnum']) {
      const perfilMap: Record<string, number> = {
        ADMIN: 1,
        CLIENTE: 2,
        EFETIVO: 3,
      };
      const valStr = String(mapped['TipoPerfilEnum']).toUpperCase();
      mapped['TipoPerfilEnum'] = perfilMap[valStr] ?? mapped['TipoPerfilEnum'];
    }
    return super.getAll(mapped);
  }

  criarConta(dto: FormData): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/CriarConta`, dto, {
      context: new HttpContext().set(SKIP_GLOBAL_ERROR_TOAST, true),
    });
  }

  atualizarConta(id: number, fd: FormData): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/${id}/AtualizarConta`, fd);
  }
}
